import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatPhoneRu } from '../../shared/lib/phone';
import { Button } from '../../shared/ui/Button';
import { GeneratedPasswordField } from '../../shared/ui/GeneratedPasswordField';
import { TextField, inputClass } from '../../shared/ui/TextField';
import { Card, PageHeader } from '../../shared/ui/primitives';
import type { Role } from '../auth/api';
import { createUser } from './api';

const ROLES: Role[] = ['doctor', 'admin'];
const roleLabel: Record<string, string> = { admin: 'Администратор', doctor: 'Врач' };

interface FormValues {
  fullName: string;
  email: string;
  password: string;
  phone: string;
  role: Role;
  sendCredentials: boolean;
}

/** Регистрация отключена — учётные записи заводит только администратор. */
export function AdminUserCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { control, register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      phone: '',
      role: 'doctor',
      sendCredentials: true,
    },
  });
  const password = watch('password');

  const create = useMutation({
    mutationFn: (v: FormValues) => createUser(v),
    onSuccess: async (user) => {
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      // Статус письма покажет карточка пользователя (см. CredentialsEmailNotice).
      navigate(`/admin/doctors/${user.id}`, {
        replace: true,
        state: { credentialsEmail: user.credentialsEmail },
      });
    },
  });

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <Link to="/admin/doctors" className="text-sm font-medium text-accent hover:text-accent-600">
          ← К списку врачей
        </Link>
        <PageHeader title="Новая учётная запись" />
      </div>

      <Card className="max-w-xl space-y-3 p-4 sm:p-5">
        <form
          onSubmit={handleSubmit((v) => create.mutate(v))}
          className="grid gap-3 sm:grid-cols-2"
        >
          <TextField label="ФИО" {...register('fullName')} />
          <TextField
            label="E-mail"
            type="email"
            autoComplete="email"
            required
            {...register('email')}
          />
          <div className="sm:col-span-2">
            <Controller
              name="password"
              control={control}
              render={({ field: f }) => (
                <GeneratedPasswordField value={f.value} onChange={f.onChange} />
              )}
            />
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-ink/80">Телефон</span>
            <Controller
              name="phone"
              control={control}
              render={({ field: f }) => (
                <input
                  className={inputClass}
                  inputMode="tel"
                  placeholder="+7(___)___-__-__"
                  value={f.value}
                  onChange={(e) => f.onChange(formatPhoneRu(e.target.value))}
                />
              )}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-ink/80">Роль</span>
            <select className={inputClass} {...register('role')}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel[r]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm text-ink/80 sm:col-span-2">
            <input
              type="checkbox"
              className="size-4 accent-accent"
              {...register('sendCredentials')}
            />
            Отправить логин и пароль на e-mail
          </label>

          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <Button type="submit" disabled={create.isPending || password.length < 8}>
              {create.isPending ? 'Создание…' : 'Создать'}
            </Button>
            {password.length > 0 && password.length < 8 && (
              <span className="text-sm text-muted">Пароль не короче 8 символов</span>
            )}
            {create.isError && (
              <span className="text-sm text-danger">
                {apiErrorMessage(create.error, 'Не удалось создать учётную запись')}
              </span>
            )}
          </div>
        </form>
      </Card>
    </section>
  );
}
